import { isString, pick, isNumber } from 'lodash-es';
import { nanoid } from 'nanoid';
import type { CoreTypes } from '../../../core';
import type { Types, UnionToAllPossibleTuples } from '../types';
import type { FromInterface } from '../../../common/types';


type C_Settings = Pick<CoreTypes.Serialized.Settings, 'discretization' | 'numDays' | 'dayStart' | 'dayEnd'>;
type C_Location = Omit<CoreTypes.Serialized.Location, '_id'>;
type C_Group    = Omit<CoreTypes.Serialized.Group,    '_id'>;
type C_Teacher  = Omit<CoreTypes.Serialized.Teacher,  '_id'>;
type C_Course   = Omit<CoreTypes.Serialized.Course,   '_id'>;
type C_Person   = Omit<CoreTypes.Serialized.Person,   '_id'>;
type C_Event    = Omit<CoreTypes.Serialized.Event,    '_id'>;
type C_LinkedEventsSet = Omit<CoreTypes.Serialized.LinkedEventsSet, '_id'>;

type Out = {
  settings:  C_Settings;
  locations: C_Location[];
  teachers:  C_Teacher[];
  persons:   C_Person[];
  groups:    C_Group[];
  courses:   C_Course[];
  events:    C_Event[];
  linkedEventsSets: C_LinkedEventsSet[]; // Just add it here to support typing, don't think this v1 is used
  meta?: {
    warnings?: string[];
    // errors?:   string[]; // TODO: implement error handling
  };
};

function generateId<T> (map: Map<string, T>) {
  // generate a unique id
  let id: string;
  do {
    id = nanoid();
  } while (map.has(id));

  return id;
}

function toMultipleOf5 (val: number | string) {
  const num = isString(val) ? parseInt(val) : val;

  // throw error if nan
  if (isNaN(num)) throw new Error(`toMultipleOf5: ${val} is not a number`);

  return Math.floor(num / 5) * 5;
}

function toInteger (val: number | string) {
  const num = isString(val) ? parseInt(val) : val;

  // throw error if nan
  if (isNaN(num)) throw new Error(`toInteger: ${val} is not a number`);

  return num;
}

function toString<T extends string | number | undefined, R = T extends undefined ? (string | undefined) : string> (x: T): R {
  if (x == undefined) return undefined as R;
  if (typeof x == 'string' && !x.length) return undefined as R;
  if (isNumber(x)) return x.toString() as R;
  return x.trim() as R;
}


function _schedules (
  source: Types.Source
): Out {
  // gather all import related warnings/errors
  const warnings: string[] = [];
  // let errors:   string[] = [];


  ////
  //// filter to only the fields we need and remove empty rows
  ////
  const locationKeys: UnionToAllPossibleTuples<keyof Types.Location> = ['name'];
  const teacherKeys:  UnionToAllPossibleTuples<keyof Types.Teacher>  = ['name'];
  const studentKeys:  UnionToAllPossibleTuples<keyof Types.Student>  = ['first_name', 'last_name', 'SSN', 'sex', 'group'];
  const groupKeys:    UnionToAllPossibleTuples<keyof Types.Group>    = ['name'];
  const courseKeys:   UnionToAllPossibleTuples<keyof Types.Course>   = ['name', 'subject', 'groups', 'teachers', 'locations', 'total_hours', 'minutes_per_week', 'lesson_durations'];
  source.locations = source.locations?.map(x => pick(x, locationKeys)).filter(x => Object.values(x).some(y => y !== undefined));
  source.teachers  = source.teachers ?.map(x => pick(x, teacherKeys )).filter(x => Object.values(x).some(y => y !== undefined));
  source.students  = source.students ?.map(x => pick(x, studentKeys )).filter(x => Object.values(x).some(y => y !== undefined));
  source.groups    = source.groups   ?.map(x => pick(x, groupKeys   )).filter(x => Object.values(x).some(y => y !== undefined));
  source.courses   = source.courses  ?.map(x => pick(x, courseKeys  )).filter(x => Object.values(x).some(y => y !== undefined));


  ////
  //// settings
  ////
  const settings: C_Settings = {
    discretization: 5,
    numDays:        5,
    dayStart:       '08:00',
    dayEnd:         '17:00',
  };
  if (!source.settings) {
    warnings.push('Could not find the settings tab.');
  }
  if (source.settings?.[0]?.number_of_days) {
    const numDays = source.settings?.[0]?.number_of_days;
    try {
      if      (isNumber(numDays)) settings.numDays = numDays;
      else if (isString(numDays)) settings.numDays = parseInt(numDays);

      // must be 5 or 7
      if (settings.numDays !== 5 && settings.numDays !== 7) {
        warnings.push(`The number of days "${numDays}" is not 5 or 7.`);
        settings.numDays = 5;
      }
    } catch {
      warnings.push(`Could not parse the number of days "${numDays}" for the settings.`);
    }
  }


  ////
  //// locations
  ////
  const locationsIdMap   = new Map<string, C_Location>();
  const locationsNameMap = new Map<string, C_Location>();
  if (!source.locations) {
    warnings.push('Could not find the locations tab.');
  }
  source.locations?.forEach(x => {
    const displayName = toString(x.name);
    if (!displayName) {
      warnings.push('Skipping a location without a name.');
      return;
    }

    if (locationsNameMap.has(displayName.toLowerCase())) {
      warnings.push(`The location "${displayName}" is defined multiple times.`);
      return;
    }

    const id = generateId(locationsIdMap);
    const location: C_Location = { id, displayName };
    locationsNameMap.set(displayName.toLowerCase(), location);
    locationsIdMap  .set(id,                        location);
  });


  ////
  //// groups
  ////
  const groupsIdMap   = new Map<string, C_Group>();
  const groupsNameMap = new Map<string, C_Group>();
  if (!source.groups) {
    warnings.push('Could not find the groups tab.');
  }
  source.groups?.forEach(x => {
    const displayName = toString(x.name);
    if (!displayName) {
      warnings.push('Skipping a group without a name.');
      return;
    }

    if (groupsNameMap.has(displayName.toLowerCase())) {
      warnings.push(`The group "${displayName}" is defined multiple times.`);
      return;
    }

    const id = generateId(groupsIdMap);
    const group = { id, displayName, species: 'class' } as C_Group;
    groupsNameMap.set(displayName.toLowerCase(), group);
    groupsIdMap  .set(id,                        group);
  });


  ////
  //// teachers
  ////
  const teachersIdMap   = new Map<string, C_Teacher>();
  const teachersNameMap = new Map<string, C_Teacher>();
  if (!source.teachers) {
    warnings.push('Could not find the teachers tab.');
  }
  source.teachers?.forEach(x => {
    const displayName = toString(x.name);
    if (!displayName) {
      warnings.push('Skipping a teacher without a name.');
      return;
    }

    if (teachersNameMap.has(displayName.toLowerCase())) {
      warnings.push(`The teacher "${displayName}" is defined multiple times.`);
      return;
    }

    const id = generateId(teachersIdMap);
    const teacher: C_Teacher = { id, displayName };
    teachersNameMap.set(displayName.toLowerCase(), teacher);
    teachersIdMap  .set(id,                        teacher);
  });


  ////
  //// students
  ////
  const personsIdMap = new Map<string, C_Person>();
  if (!source.students) {
    warnings.push('Could not find the students tab.');
  }
  source.students?.forEach(x => {
    const firstName = toString(x.first_name);
    const lastName  = toString(x.last_name );
    const SSN       = toString(x.SSN       );

    let groupReference: string | undefined = undefined;
    if (x.group) {
      const key = toString(x.group).toLowerCase();
      const ref = groupsNameMap.get(key);    /// to lower case only!
      if (!ref) {
        warnings.push(`The group ${x.group} referenced from the student "${firstName} ${lastName}" does not exist.`);
      } else {
        groupReference = ref.id;
      }
    }

    let sex: C_Person['sex'];
    if (x.sex) {
      if      (isString(x.sex) && x.sex.toLowerCase() == 'man')   sex = 'Man';
      else if (isString(x.sex) && x.sex.toLowerCase() == 'woman') sex = 'Woman';
      else {
        warnings.push(`Unknown sex "${ x.sex }" for the student "${firstName} ${lastName}".`);
      }
    }

    const id = generateId(personsIdMap);
    const person: C_Person = { id, firstName, lastName, sex, group: groupReference, ...SSN && { SSN: { value: SSN } } };
    personsIdMap.set(id, person);
  });


  ////
  //// courses
  ////
  const coursesIdMap = new Map<string, C_Course>();
  const eventsIdMap  = new Map<string, C_Event>();
  if (!source.courses) {
    warnings.push('Could not find the courses tab.');
  }
  source.courses?.forEach(x => {
    const courseId    = generateId(coursesIdMap);
    const displayName = toString(x.name)?.trim();
    const subject     = toString(x.subject);

    // try to parse the total hours as an integer
    let totalHours: number | undefined = undefined;
    if (x.total_hours) {
      try {
        totalHours = toInteger(x.total_hours);
      } catch {
        warnings.push(`The total hours "${x.total_hours}" for the course "${displayName}" is not an integer.`);
      }
    }

    // try to parse the minutes per week as an multiple of 5
    let minutesPerWeek: number | undefined = undefined;
    if (x.minutes_per_week) {
      try {
        minutesPerWeek = toMultipleOf5(x.minutes_per_week);
      } catch {
        warnings.push(`The minutes per week "${x.minutes_per_week}" for the course "${displayName}" is not an integer.`);
      }
    }

    // try to parse the lesson durations as an array of integers from which we will generate events
    if (x.lesson_durations) {
      try {
        let durations: number[] = [];
        if      (isNumber(x.lesson_durations)) durations = [x.lesson_durations];
        else if (isString(x.lesson_durations)) durations = x.lesson_durations.split(',').map(x => x.trim())
          .map(x => parseInt(x));

        // truncate to multiple of 5 and filter out zero duration events
        durations = durations
          .map(x => {
            try {
              return toMultipleOf5(x);
            } catch {
              warnings.push(`The lesson duration "${x}" for the course "${displayName}" is not an integer.`);
              return undefined;
            }
          })
          .filter((x): x is number => {
            if (!x) {
              warnings.push(`The lesson duration for the course "${displayName}" becomes 0 when truncated to the nearest multiple of 5.`);
              return false;
            }
            return true;
          });

        durations.forEach(x => {
          const id = generateId(eventsIdMap);
          const event: C_Event = { id, course: courseId, preferredDuration: x };
          eventsIdMap.set(id, event);
        });
      } catch {
        warnings.push(`Could not parse the lesson durations "${x.lesson_durations}" for the course "${displayName}".`);
      }
    }

    // locations
    const locationReferences: { groupIndex: number; to: string }[] = [];

    toString(x.locations)?.split('+')
      .flatMap((x, i) => x.split(',').map(x => [i, x.trim().toLowerCase()] as [number, string]))
      .forEach(([i, x]) => {
        const ref = locationsNameMap.get(x);
        if (!ref) {
          warnings.push(`The location "${x}" referenced from the course "${displayName}" does not exist.`);
          return;
        }

        if (locationReferences.some(y => y.to == ref.id)) {
          warnings.push(`The location "${x}" is referenced multiple times from the course "${displayName}".`);
          return;
        }

        locationReferences.push({ groupIndex: i, to: ref.id });
      });

    // groups
    const groupReferences: { to: string }[] = [];
    toString(x.groups)?.split(',')
      .map(x => x.trim().toLowerCase())
      .forEach(x => {
        const ref = groupsNameMap.get(x);
        if (!ref) {
          warnings.push(`The group "${x}" referenced from the course "${displayName}" does not exist.`);
          return;
        }

        if (groupReferences.some(y => y.to == ref.id)) {
          warnings.push(`The group "${x}" is referenced multiple times from the course "${displayName}".`);
          return;
        }

        groupReferences.push({ to: ref.id });
      });

    // teachers
    const teacherReferences: { to: string }[] = [];
    toString(x.teachers)?.split(',')
      .map(x => x.trim().toLowerCase())
      .forEach(x => {
        const ref = teachersNameMap.get(x);
        if (!ref) {
          warnings.push(`The teacher "${x}" referenced from the course "${displayName}" does not exist.`);
          return;
        }

        if (teacherReferences.some(y => y.to == ref.id)) {
          warnings.push(`The teacher "${x}" is referenced multiple times from the course "${displayName}".`);
          return;
        }

        teacherReferences.push({ to: ref.id });
      });

    const course: C_Course = {
      id:                 courseId,
      groups:             groupReferences,
      teachers:           teacherReferences,
      locations:          locationReferences.map(x => ({ groupIndex: x.groupIndex, locations: [x.to] })),
      expectedTotalHours: totalHours,
      plannedDuration:    totalHours ? `${totalHours} hrs` : undefined,
      displayName, subject, minutesPerWeek,
    };
    coursesIdMap.set(courseId, course);
  });


  ////
  //// return
  ////
  return {
    settings,
    locations: [...locationsIdMap.values()],
    teachers:  [...teachersIdMap .values()],
    persons:   [...personsIdMap  .values()],
    groups:    [...groupsIdMap   .values()],
    courses:   [...coursesIdMap  .values()],
    events:    [...eventsIdMap   .values()],
    linkedEventsSets: [], // linked events are not supported in v1, so just return an empty array
    meta:      {
      // ...errors  .length && { errors:   errors   },
      ...warnings.length && { warnings: warnings }
    }
  };
}


export default {
  schedules: _schedules
} satisfies FromInterface;